import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { Sidebar } from './Sidebar';
import { TaskMonitor } from './TaskMonitor';
import {
  selectActiveTaskIds,
  useTaskProcessingStore,
} from '../../stores/taskProcessing.store';
import { apiService } from '../../services/api';
import { TaskNotifications } from './TaskNotifications';
import { ReportJobMonitor } from '../../features/reports/components/ReportJobMonitor';

const AppWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  background: var(--surface-strong);
  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  margin-left: 15rem;
  padding: 2rem;
  @media (max-width: 1023px) {
    padding: 1.5rem;
  }
  @media (max-width: 767px) {
    margin-left: 0;
    padding: 1.5rem 1rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  min-width: 0;
`;

/** Shared page gutter and responsive navigation for authenticated views. */
export const AppLayout: React.FC = () => {
  const hydrateTasks = useTaskProcessingStore((state) => state.hydrateTasks);
  const activeTaskIds = useTaskProcessingStore(selectActiveTaskIds);
  const hasHydratedRef = React.useRef(false);
  const isHydratingRef = React.useRef(false);

  useEffect(() => {
    // Only hydrate once on mount, and only if not already hydrated/hydrating
    if (
      activeTaskIds.length === 0 &&
      !hasHydratedRef.current &&
      !isHydratingRef.current
    ) {
      isHydratingRef.current = true;

      apiService
        .getActiveTasks()
        .then((tasks) => {
          hydrateTasks(tasks);
          hasHydratedRef.current = true;
        })
        .catch((error) => {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[AppLayout] Failed to hydrate active tasks', error);
          }
        })
        .finally(() => {
          isHydratingRef.current = false;
        });
    }
  }, [activeTaskIds.length, hydrateTasks]);

  return (
    <AppWrapper>
      <a className="skip-link" href="#main-content">
        Ir al contenido
      </a>
      <Sidebar />
      <MainContent id="main-content" tabIndex={-1}>
        <TaskMonitor />
        <ReportJobMonitor />
        <TaskNotifications />
        <ContentContainer>
          <Outlet />
        </ContentContainer>
      </MainContent>
    </AppWrapper>
  );
};
